import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { ArrowLeft, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/shared'
import { emailSegmentService } from '@/services/emailSegmentService'
import { emailCustomFieldService } from '@/services/emailCustomFieldService'
import SegmentRuleBuilder from '@/components/MarketingEmail/SegmentRuleBuilder'

const SegmentEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditMode = !!id

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [rules, setRules] = useState([])
  const [previewCount, setPreviewCount] = useState(null)
  const [previewSample, setPreviewSample] = useState([])
  const [previewLoading, setPreviewLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const { data: fieldsData } = useQuery('email-custom-fields', emailCustomFieldService.getAllFields)
  const customFieldDefinitions = fieldsData?.data?.fields || []

  const { data: segmentData, isLoading } = useQuery(
    ['email-segment', id],
    () => emailSegmentService.getSegmentById(id),
    { enabled: isEditMode }
  )

  useEffect(() => {
    const segment = segmentData?.data?.segment
    if (segment) {
      setName(segment.name)
      setDescription(segment.description || '')
      setRules(segment.rules || [])
    }
  }, [segmentData])

  // Live preview count, debounced
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (rules.length === 0) {
        setPreviewCount(null)
        setPreviewSample([])
        return
      }
      setPreviewLoading(true)
      try {
        const res = await emailSegmentService.previewRules(rules)
        setPreviewCount(res.data.count)
        setPreviewSample(res.data.sample || [])
      } catch {
        setPreviewCount(null)
        setPreviewSample([])
      } finally {
        setPreviewLoading(false)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [rules])

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Segment name is required')
      return
    }
    setSaving(true)
    try {
      const payload = { name, description, rules }
      if (isEditMode) {
        await emailSegmentService.updateSegment(id, payload)
        toast.success('Segment updated')
      } else {
        await emailSegmentService.createSegment(payload)
        toast.success('Segment created')
      }
      queryClient.invalidateQueries('email-segments')
      navigate('/marketing-email/segments')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save segment')
    } finally {
      setSaving(false)
    }
  }

  if (isEditMode && isLoading) {
    return <div className="text-center py-12 text-muted-foreground">Loading...</div>
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditMode ? 'Edit Segment' : 'New Segment'}
        description="Define an audience using rule-based conditions"
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/marketing-email/segments')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Segments
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Active Gold Members" />
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is this segment used for?" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Audience Rules</CardTitle>
            </CardHeader>
            <CardContent>
              <SegmentRuleBuilder rules={rules} onChange={setRules} customFieldDefinitions={customFieldDefinitions} />
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground font-medium">
                Sample Contacts
                {rules.length > 0 && !previewLoading && previewCount !== null
                  ? ` (${previewSample.length} of ${previewCount})`
                  : ''}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {previewLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : rules.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10 px-6">
                  Add a condition to see matching contacts
                </p>
              ) : previewSample.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10 px-6">
                  No contacts match these conditions
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {previewSample.map((contact) => (
                    <li key={contact.id} className="px-6 py-2.5">
                      <p className="text-sm font-medium truncate">
                        {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || '—'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{contact.email}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Button className="w-full mt-4" onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditMode ? 'Save Changes' : 'Create Segment'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SegmentEditor
